import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bot, KeyRound, CheckCircle2, Palette } from "lucide-react";

const PRESETS = {
  "Azul EMAURAN": { header: "#0a1224", accent: "#1d4e8c", zebra: "#eef2f8", grid: "#c7d2e0" },
  "Naranja OOMINING": { header: "#7c2d12", accent: "#b45309", zebra: "#fff7ed", grid: "#e7d3c0" },
  "Gris ejecutivo": { header: "#27272a", accent: "#3f3f46", zebra: "#fafafa", grid: "#cccccc" },
  "Verde minería": { header: "#14532d", accent: "#15803d", zebra: "#f0fdf4", grid: "#cfe7d6" },
  "Alto contraste": { header: "#000000", accent: "#b91c1c", zebra: "#f5f5f5", grid: "#999999" },
};
const FIELDS = [
  { key: "header", label: "Encabezado de tabla" },
  { key: "accent", label: "Títulos / acentos" },
  { key: "zebra", label: "Fila alterna" },
  { key: "grid", label: "Líneas de tabla" },
];

export default function SettingsView() {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [colors, setColors] = useState(null);
  const [savingColors, setSavingColors] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setColors(data.pdf_colors);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey.trim()) { toast.error("Ingresa tu API key de Gemini"); return; }
    setBusy(true);
    try {
      await api.put("/settings/gemini-key", { api_key: apiKey.trim() });
      toast.success("API key de Gemini guardada");
      setApiKey(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveColors = async () => {
    setSavingColors(true);
    try {
      const { data } = await api.put("/settings/pdf-colors", colors);
      setColors(data.pdf_colors);
      toast.success("Colores del PDF guardados");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingColors(false); }
  };

  if (!settings || !colors) return <div className="p-10 text-sm text-muted-foreground">Cargando ajustes…</div>;

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="settings-view">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <h1 className="font-head font-black text-xl tracking-tight">IA & Ajustes</h1>
        </div>

        {/* Gemini */}
        <div className="border border-border rounded-sm p-5 space-y-4">
          <div>
            <h2 className="text-base font-medium flex items-center gap-2"><KeyRound className="h-4 w-4" /> API Key de Google Gemini</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Necesaria para el Cazador de Licitaciones IA. Obténla en{" "}
              <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="text-primary underline">Google AI Studio</a>.
              Modelo: <span className="font-mono-num">{settings.gemini_model}</span>.
            </p>
          </div>
          {settings.gemini_key_set ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600" data-testid="gemini-key-status">
              <CheckCircle2 className="h-4 w-4" /> API key configurada. Puedes actualizarla abajo.
            </div>
          ) : (
            <div className="text-sm text-amber-600" data-testid="gemini-key-status">Aún no has configurado tu API key.</div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">API Key</Label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder="AIza..." className="h-9 font-mono-num rounded-sm" data-testid="gemini-key-input" />
          </div>
          <Button onClick={save} disabled={busy} className="rounded-sm gap-2" data-testid="gemini-key-save">
            <KeyRound className="h-4 w-4" /> Guardar API key
          </Button>
        </div>

        {/* PDF Colors */}
        <div className="border border-border rounded-sm p-5 space-y-4" data-testid="pdf-colors-card">
          <div>
            <h2 className="text-base font-medium flex items-center gap-2"><Palette className="h-4 w-4" /> Colores del presupuesto en PDF</h2>
            <p className="text-sm text-muted-foreground mt-1">Personaliza la combinación de colores del PDF exportado.</p>
          </div>

          <div>
            <Label className="text-xs">Paletas rápidas</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {Object.entries(PRESETS).map(([name, p]) => (
                <button key={name} onClick={() => setColors(p)} data-testid={`preset-${name}`}
                  className="flex items-center gap-2 text-xs border border-border rounded-sm px-2.5 py-1.5 hover:bg-accent/40">
                  <span className="flex">
                    {["header", "accent", "zebra", "grid"].map((k) => (
                      <span key={k} className="h-3.5 w-3.5 rounded-[2px] border border-border/50" style={{ background: p[k] }} />
                    ))}
                  </span>
                  {name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-xs">{f.label}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={colors[f.key]} onChange={(e) => setColors({ ...colors, [f.key]: e.target.value })}
                    className="h-9 w-12 rounded-sm border border-border bg-transparent cursor-pointer" data-testid={`color-${f.key}`} />
                  <Input value={colors[f.key]} onChange={(e) => setColors({ ...colors, [f.key]: e.target.value })}
                    className="h-9 font-mono-num rounded-sm uppercase" />
                </div>
              </div>
            ))}
          </div>

          {/* Live preview */}
          <div className="rounded-sm border border-border overflow-hidden text-[11px]">
            <div className="px-2 py-1 font-medium" style={{ background: colors.accent, color: "#fff" }}>E-2 · Presupuesto (vista previa)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
              {["Clave", "Concepto", "Importe"].map((h) => (
                <div key={h} style={{ background: colors.header, color: "#fff", borderRight: `1px solid ${colors.grid}` }} className="px-2 py-1">{h}</div>
              ))}
              {[0, 1].map((r) => ["A-00" + (r + 1), "Concepto de obra", "$ 1,234.56"].map((c, ci) => (
                <div key={r + "-" + ci} style={{ background: r % 2 ? colors.zebra : "#fff", borderRight: `1px solid ${colors.grid}`, borderTop: `1px solid ${colors.grid}` }} className="px-2 py-1">{c}</div>
              )))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={saveColors} disabled={savingColors} className="rounded-sm gap-2" data-testid="save-pdf-colors">
              <Palette className="h-4 w-4" /> Guardar colores
            </Button>
            <Button variant="outline" onClick={() => setColors(settings.pdf_colors_default)} className="rounded-sm" data-testid="reset-pdf-colors">
              Restablecer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
